import { DOCKER_IMAGE_TAG } from '@config/environment';
import { userSelector } from '@data/selectors/authSelectors';
import { logout } from '@data/slices/authSlice';
import { useAppDispatch } from '@hooks/reduxHooks';
import {
  Button,
  Spacer,
  theme,
  UserIcon,
  VersionIcon,
  Subtitle,
  mq,
  PhoneIcon,
} from '@kvdbil/components';
import { responsivePadding } from '@utils/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import AccountInformationRow from './components/AccountRow';
import UtilityButton from './components/UtilityButton';
import { windowExists } from '@shared/utils';
import { isDevelopment } from '@config/constants';

const Container = styled.div`
  background-color: ${theme.colors.background.light};
  ${responsivePadding}
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1rem;
`;

const AccountRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  text-align: left;
  justify-content: items;
  align-items: center;
  gap: 3rem;

  & > button {
    margin-right: auto;
  }

  ${mq(null, 'tablet')} {
    display: flex;
    flex-direction: column;
    align-items: items;
    gap: 0;
    margin-bottom: 0.5rem;

    & > button {
      margin: 0 auto;
      margin-top: 0.5rem;
    }
  }
`;

const AccountView = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const user = useSelector(userSelector);

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleCallSupport = (phoneNumber: string) => {
    if (windowExists()) window.open(`tel:${phoneNumber}`);
  };

  const missingVersionText = isDevelopment
    ? t('Running live on localhost')
    : '--';
  const appVersion = DOCKER_IMAGE_TAG ? DOCKER_IMAGE_TAG : missingVersionText;

  return (
    <Container>
      <Subtitle>{t('Account')}</Subtitle>
      <Spacer />
      <AccountInformationRow
        title={user?.email}
        subtitle={t('Logged in account')}
        icon={<UserIcon />}
      />

      <Button color="info" onClick={handleLogout} variant="flat">
        {t('Logout')}
      </Button>

      <AccountInformationRow
        title={appVersion}
        subtitle={t('App version')}
        icon={<VersionIcon />}
      />

      <Spacer />

      <Subtitle>{t('Tools')}</Subtitle>

      <Spacer />

      <AccountRow>
        <AccountInformationRow
          title={t('Call support')}
          subtitle={t(
            `Have you run into problems? Contact us and we'll do our best to help you.`
          )}
          icon={<PhoneIcon />}
        />

        <UtilityButton
          buttonProps={{
            children: t('Contact us'),
            color: 'info',
          }}
          dialogProps={{
            title: t('Call support'),
            description: t(
              `You are about to call support. Press the button below to proceed.`
            ),
            buttonProps: {
              onClick: () => handleCallSupport(t('SUPPORT_PHONE_NUMBER')), // Update phone number in Phrase
              children: t('Call support'),
              prefixIcon: <PhoneIcon />,
              color: 'info',
              id: 'call-support-button',
            },
          }}
        />
      </AccountRow>
    </Container>
  );
};

export default AccountView;
